/**
 * Reverse Geocoding Service
 * Converts GPS coordinates to readable location names using OpenStreetMap Nominatim API
 */

export interface LocationInfo {
  city?: string
  country?: string
  state?: string
  formattedAddress?: string
  coordinates: {
    latitude: number
    longitude: number
  }
}

export interface NominatimResponse {
  display_name?: string
  address?: {
    city?: string
    town?: string
    village?: string
    county?: string
    state?: string
    country?: string
    country_code?: string
  }
}

export class ReverseGeocodingService {
  private cache = new Map<string, LocationInfo>()
  private readonly baseUrl = 'https://nominatim.openstreetmap.org/reverse'
  private readonly requestDelay = 1000 // 1 second delay between requests to respect rate limits

  async getLocationInfo(latitude: number, longitude: number): Promise<LocationInfo | null> {
    const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!
    }

    try {
      // Add delay to respect Nominatim usage policy
      await this.delay(this.requestDelay)
      
      const response = await fetch(
        `${this.baseUrl}?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1&accept-language=en`,
        {
          headers: {
            'User-Agent': 'StockTagger.ai (https://stocktagger.ai)', // Required by Nominatim
          }
        }
      )

      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`)
      }

      const data: NominatimResponse = await response.json()
      const locationInfo = this.parseLocationInfo(data, latitude, longitude)
      
      // Cache the result
      this.cache.set(cacheKey, locationInfo)
      
      return locationInfo
    } catch (error) {
      console.warn('Failed to geocode coordinates:', error)
      
      // Return basic info with just coordinates if geocoding fails
      const fallbackInfo: LocationInfo = {
        coordinates: { latitude, longitude },
        formattedAddress: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
      }
      
      this.cache.set(cacheKey, fallbackInfo)
      return fallbackInfo
    }
  }

  private parseLocationInfo(data: NominatimResponse, latitude: number, longitude: number): LocationInfo {
    const address = data.address || {}
    
    // Extract city/town (prioritize city > town > village)
    const city = address.city || address.town || address.village || address.county
    
    // Extract state/region
    const state = address.state
    
    // Extract country
    const country = address.country
    
    // Create formatted address
    let formattedAddress = ''
    if (city && country) {
      formattedAddress = state ? `${city}, ${state}, ${country}` : `${city}, ${country}`
    } else if (country) {
      formattedAddress = country
    } else if (data.display_name) {
      // Fallback to display name, but shorten it
      const parts = data.display_name.split(',').slice(0, 3)
      formattedAddress = parts.join(', ')
    } else {
      formattedAddress = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
    }

    return {
      city,
      state,
      country,
      formattedAddress,
      coordinates: { latitude, longitude }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // Clear cache (useful for testing or memory management)
  clearCache(): void {
    this.cache.clear()
  }

  // Get cache size (for debugging)
  getCacheSize(): number {
    return this.cache.size
  }
}

// Singleton instance
export const reverseGeocodingService = new ReverseGeocodingService()